import { createContext, useContext, useMemo, useState, useCallback } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { easeOut } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EJS_SERVICE = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EJS_TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EJS_PUBLIC = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const roleLabels = {
  builder: "Builder",
  channel_partner: "Channel Partner",
  general: "Early Access",
};

const EarlyAccessContext = createContext(null);
export const useEarlyAccess = () => useContext(EarlyAccessContext);

export function EarlyAccessProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState("general");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "" });

  const openDialog = useCallback((r = "general") => {
    setRole(r);
    setDone(false);
    setForm({ name: "", email: "", company: "" });
    setOpen(true);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/early-access`, {
        email: form.email,
        name: form.name,
        company: form.company,
        role,
      });

      if (EJS_SERVICE && EJS_TEMPLATE && EJS_PUBLIC) {
        try {
          await emailjs.send(
            EJS_SERVICE,
            EJS_TEMPLATE,
            {
              name: form.name || "there",
              email: form.email,
              company: form.company || "-",
              role: roleLabels[role],
            },
            { publicKey: EJS_PUBLIC }
          );
        } catch (err) {
          console.warn("EmailJS send skipped:", err?.text || err);
        }
      }
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(() => ({ open: openDialog }), [openDialog]);

  return (
    <EarlyAccessContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="early-access-modal"
          >
            <motion.div
              className="absolute inset-0 bg-[#0b1220]/50 backdrop-blur-sm"
              onClick={() => setOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <motion.div
              className="relative w-full max-w-md rounded-[24px] border border-[#e5e7eb] bg-white p-8 shadow-[0_40px_120px_-30px_rgba(30,58,138,0.4)]"
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.4, ease: easeOut }}
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute right-5 top-5 text-[#64748b] transition-colors hover:text-[#111827]"
                data-testid="early-access-close-button"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              {!done ? (
                <>
                  <p className="eyebrow text-[#3b82f6]">{roleLabels[role]}</p>
                  <h3 className="mt-3 text-2xl font-extrabold tracking-tight text-[#111827]">
                    Join the early access list
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#64748b]">
                    Get product updates, priority access, and a say in what we
                    build next.
                  </p>

                  <form onSubmit={submit} className="mt-6 space-y-3.5">
                    <input
                      type="text"
                      placeholder="Full name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full rounded-xl border border-[#e5e7eb] bg-[#f5f7fb] px-4 py-3 text-sm text-[#111827] outline-none transition-all placeholder:text-[#94a3b8] focus:border-[#3b82f6] focus:bg-white focus:ring-4 focus:ring-[#3b82f6]/10"
                      data-testid="early-access-name-input"
                    />
                    <input
                      type="email"
                      required
                      placeholder="Work email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full rounded-xl border border-[#e5e7eb] bg-[#f5f7fb] px-4 py-3 text-sm text-[#111827] outline-none transition-all placeholder:text-[#94a3b8] focus:border-[#3b82f6] focus:bg-white focus:ring-4 focus:ring-[#3b82f6]/10"
                      data-testid="early-access-email-input"
                    />
                    <input
                      type="text"
                      placeholder="Company (optional)"
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className="w-full rounded-xl border border-[#e5e7eb] bg-[#f5f7fb] px-4 py-3 text-sm text-[#111827] outline-none transition-all placeholder:text-[#94a3b8] focus:border-[#3b82f6] focus:bg-white focus:ring-4 focus:ring-[#3b82f6]/10"
                      data-testid="early-access-company-input"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary group mt-1 flex w-full items-center justify-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white transition-all disabled:opacity-70"
                      data-testid="early-access-submit-button"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          Request access
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </>
                      )}
                    </button>
                  </form>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-6 text-center"
                  data-testid="early-access-success"
                >
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#3b82f6]/10">
                    <CheckCircle2 className="h-7 w-7 text-[#1e3a8a]" />
                  </div>
                  <h3 className="mt-5 text-2xl font-extrabold tracking-tight text-[#111827]">
                    You're on the list
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#64748b]">
                    Thanks for joining. We'll reach out with priority access as
                    LintelGrid takes shape.
                  </p>
                  <button
                    onClick={() => setOpen(false)}
                    className="btn-primary mt-6 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white"
                    data-testid="early-access-done-button"
                  >
                    Done
                  </button>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </EarlyAccessContext.Provider>
  );
}
